import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";

// Keep menu scrolled to the top when it opens
const MenuList = (props) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
  }, []);
  return <components.MenuList {...props} innerRef={ref} />;
};

const MAX_EX_NAME = 40;
const sanitizeExerciseName = (s) =>
  s
    .replace(/[^A-Za-z0-9 ]+/g, "") // remove non-alphanumeric (keep spaces)
    .replace(/\s+/g, " ") // collapse spaces
    .trim()
    .slice(0, MAX_EX_NAME);

export default function AddWorkoutModal({
  isOpen,
  onClose,
  onSaved,
  mode = "create",          // NEW: "create" | "edit"
  initialWorkout = null,    // NEW: workout data when editing
}) {
  const [exerciseOptions, setExerciseOptions] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isExerciseModalOpen, setIsExerciseModalOpen] = useState(false);
  // [{ uid, id, name, sets:[{lbs, reps}]}]
  const [workoutExercises, setWorkoutExercises] = useState([]);
  const [menuKey, setMenuKey] = useState(0); // re-mount Select each open

  // Close confirmations
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isFinishConfirmOpen, setIsFinishConfirmOpen] = useState(false);

  // Saving + validity
  const [saving, setSaving] = useState(false);

  // Validation UX state
  const [attemptedFinish, setAttemptedFinish] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  // Scroll ref for content (to reveal banner on invalid)
  const contentRef = useRef(null);

  // Helper: are all sets filled with digits?
  const allSetsHaveNumbers = useCallback(
    () =>
      workoutExercises.length > 0 &&
      workoutExercises.every(
        (ex) =>
          ex.sets &&
          ex.sets.length > 0 &&
          ex.sets.every(
            (s) =>
              typeof s.lbs === "string" &&
              typeof s.reps === "string" &&
              /^\d+$/.test(s.lbs) &&
              /^\d+$/.test(s.reps)
          )
      ),
    [workoutExercises]
  );

  // Memoized validator so useEffect deps are accurate
  const workoutIsValid = useCallback(
    () => allSetsHaveNumbers(),
    [allSetsHaveNumbers]
  );

  // Clear banner once user fixes inputs
  useEffect(() => {
    if (attemptedFinish && workoutIsValid()) {
      setValidationMsg("");
    }
  }, [attemptedFinish, workoutIsValid]);

  // Lock page scroll when modal is open (optional polish)
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Use existing workout date for edit; today's date for create
  const rawDate =
    mode === "edit" && initialWorkout?.date
      ? new Date(initialWorkout.date)
      : new Date();

  const formattedDate = rawDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });

  // Load exercise options
  useEffect(() => {
    if (!isOpen) return;
    const fetchExercises = async () => {
      try {
        const { data } = await axios.get("/api/exercises");
        const options = data
          .map((ex) => ({ value: ex._id, label: ex.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setExerciseOptions(options);
      } catch (err) {
        console.error("Error fetching exercises:", err);
      }
    };
    fetchExercises();
  }, [isOpen]);

  // NEW: initialize exercises when opening (create vs edit)
  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && initialWorkout) {
      const mapped = (initialWorkout.exercises || []).map((ex) => ({
        uid:
          ex._id || `${ex.name}_${Math.random().toString(36).slice(2, 8)}`,
        id: ex._id || ex.name,
        name: ex.name,
        sets: (ex.sets || []).map((s) => ({
          lbs:
            s.lbs === 0 || s.lbs === "0" || s.lbs
              ? String(s.lbs)
              : "",
          reps:
            s.reps === 0 || s.reps === "0" || s.reps
              ? String(s.reps)
              : "",
        })),
      }));
      setWorkoutExercises(mapped);
      setAttemptedFinish(false);
      setValidationMsg("");
    }

    if (mode === "create") {
      setWorkoutExercises([]);
      setAttemptedFinish(false);
      setValidationMsg("");
    }
  }, [isOpen, mode, initialWorkout]);

  const handleAddExercise = () => {
    if (!selectedExercise) return;
    const uid = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setWorkoutExercises((prev) => [
      ...prev,
      {
        uid,
        id: selectedExercise.value,
        name: selectedExercise.label,
        sets: [{ lbs: "", reps: "" }],
      },
    ]);
    setIsExerciseModalOpen(false);
    setSelectedExercise(null);
  };

  const handleAddSet = (exerciseIndex) => {
    setWorkoutExercises((prev) => {
      const copy = [...prev];
      copy[exerciseIndex] = {
        ...copy[exerciseIndex],
        sets: [...copy[exerciseIndex].sets, { lbs: "", reps: "" }],
      };
      return copy;
    });
  };

  const handleDeleteSet = (exerciseIndex, setIndex) => {
    setWorkoutExercises((prev) => {
      const copy = [...prev];
      const sets = [...copy[exerciseIndex].sets];
      sets.splice(setIndex, 1);
      copy[exerciseIndex] = { ...copy[exerciseIndex], sets };
      return copy;
    });
  };

  const handleDeleteExercise = (exerciseIndex) => {
    setWorkoutExercises((prev) => prev.filter((_, i) => i !== exerciseIndex));
  };

  const handleSetChange = (exerciseIndex, setIndex, field, value) => {
    const sanitized = value.replace(/\D/g, "").slice(0, 4);
    setWorkoutExercises((prev) => {
      const copy = [...prev];
      const sets = [...copy[exerciseIndex].sets];
      sets[setIndex] = { ...sets[setIndex], [field]: sanitized };
      copy[exerciseIndex] = { ...copy[exerciseIndex], sets };
      return copy;
    });
  };

  // Close (X) -> confirm if there’s content
  const handleRequestClose = () => {
    const hasUnsaved = workoutExercises.length > 0;
    if (hasUnsaved) {
      setIsCloseConfirmOpen(true);
    } else {
      onClose();
      setSelectedExercise(null);
      setIsExerciseModalOpen(false);
    }
  };

  // Confirm and actually close the modal
  const confirmClose = () => {
    setIsCloseConfirmOpen(false);
    onClose();
    setSelectedExercise(null);
    setIsExerciseModalOpen(false);
    // setWorkoutExercises([]); // optional: clear on close
  };

  // -------- Creatable (custom exercise) helpers ----------
  const isValidNewOption = (inputValue) => {
    const cleaned = sanitizeExerciseName(inputValue);
    const normalizedInput = inputValue.replace(/\s+/g, " ").trim();
    return cleaned.length > 0 && cleaned === normalizedInput;
  };

  const handleCreateExercise = (inputValue) => {
    const name = sanitizeExerciseName(inputValue);
    if (!name) return;
    const newOption = {
      value: `custom:${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: name,
    };
    setExerciseOptions((prev) => [newOption, ...prev]);
    setSelectedExercise(newOption);
  };
  // ------------------------------------------------------

  // Ask for confirmation before saving
  const requestFinish = () => {
    setAttemptedFinish(true);

    if (!workoutIsValid()) {
      setValidationMsg(
        "Please enter weight (lbs) and reps for every set before finishing."
      );
      // reveal the banner
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    if (saving) return;
    setValidationMsg("");
    setIsFinishConfirmOpen(true);
  };

  // Confirm finish -> call real save
  const confirmFinish = async () => {
    setIsFinishConfirmOpen(false);
    await handleFinish();
  };

  // Save to API, close modal, notify parent
  const handleFinish = async () => {
    if (!workoutIsValid() || saving) return;
    try {
      setSaving(true);
      const token = localStorage.getItem("token");

      const payload = {
        // keep original date when editing, use now when creating
        date:
          mode === "edit" && initialWorkout?.date
            ? initialWorkout.date
            : new Date().toISOString(),
        exercises: workoutExercises.map((ex) => ({
          name: ex.name,
          sets: ex.sets.map((s) => ({
            lbs: Number(s.lbs || 0),
            reps: Number(s.reps || 0),
          })),
        })),
      };

      const url =
        mode === "edit" && initialWorkout?._id
          ? `/api/workouts/${initialWorkout._id}`
          : "/api/workouts";
      const method =
        mode === "edit" && initialWorkout?._id ? "put" : "post";

      const { data } = await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (onSaved) onSaved(data);
      onClose();
      // Optional: setWorkoutExercises([]);
    } catch (err) {
      console.error("Failed to save workout:", err);
    } finally {
      setSaving(false);
    }
  };

  // Field-level invalid check for red highlight when user tried to finish
  const isFieldInvalid = (val) => attemptedFinish && !/^\d+$/.test(val);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {/* Modal shell: fixed height cap + column layout */}
      {/* Responsive fix: min-width only on sm+ so it won't clip on phones */}
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg sm:min-w-[460px] max-h-[85vh] flex flex-col relative">
        {/* Header (sticky/always visible) */}
        <div className="px-6 pt-6 pb-4 sticky top-0 bg-white z-10 border-b">
          <button
            onClick={handleRequestClose}
            className="absolute top-3 right-3 text-gray-700 hover:text-black text-2xl font-bold"
          >
            ✕
          </button>
          <h2 className="text-md font-semibold">{formattedDate}</h2>
        </div>

        {/* Scrollable content */}
        <div ref={contentRef} className="px-6 py-4 overflow-y-auto flex-1">
          {/* Validation banner (brand blue) */}
          {validationMsg && (
            <div
              className="mb-3 rounded-md px-3 py-2 text-sm font-medium"
              style={{
                backgroundColor: "rgba(57, 53, 229, 0.10)", // soft #3935E5 tint
                border: "1px solid #3935E5",
                color: "#0f172a", // dark text for contrast
              }}
            >
              {validationMsg}
            </div>
          )}

          <div className="space-y-6">
            {workoutExercises.map((ex, exIdx) => (
              <div key={ex.uid} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold">{ex.name}</h3>
                  <button
                    type="button"
                    onClick={() => handleDeleteExercise(exIdx)}
                    className="text-red-600 text-sm px-2 py-1 rounded-md border border-red-600 hover:bg-red-50"
                    title="Remove Exercise"
                  >
                    Delete
                  </button>
                </div>

                <div className="grid grid-cols-4 text-sm font-semibold text-gray-700 mb-2">
                  <span>Set</span>
                  <span className="text-center">lbs</span>
                  <span className="text-center">Reps</span>
                  <span className="text-right"> </span>
                </div>

                {ex.sets.length === 0 ? (
                  <div className="text-sm text-gray-500 mb-2">
                    No sets yet. Add one below.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {ex.sets.map((set, sIdx) => (
                      <div
                        key={sIdx}
                        className="grid grid-cols-4 items-center gap-2"
                      >
                        <div className="text-sm font-medium">{sIdx + 1}</div>

                        <input
                          inputMode="numeric"
                          pattern="\d*"
                          maxLength={4}
                          value={set.lbs}
                          onChange={(e) =>
                            handleSetChange(exIdx, sIdx, "lbs", e.target.value)
                          }
                          className={`h-8 rounded-md bg-gray-200/80 focus:bg-gray-200 outline-none px-2 text-center ${
                            isFieldInvalid(set.lbs) ? "ring-2 ring-red-400" : ""
                          }`}
                          placeholder="0"
                        />

                        <input
                          inputMode="numeric"
                          pattern="\d*"
                          maxLength={4}
                          value={set.reps}
                          onChange={(e) =>
                            handleSetChange(exIdx, sIdx, "reps", e.target.value)
                          }
                          className={`h-8 rounded-md bg-gray-200/80 focus:bg-gray-200 outline-none px-2 text-center ${
                            isFieldInvalid(set.reps) ? "ring-2 ring-red-400" : ""
                          }`}
                          placeholder="0"
                        />

                        <button
                          type="button"
                          onClick={() => handleDeleteSet(exIdx, sIdx)}
                          className="justify-self-end text-red-600 hover:text-black text-sm px-2 py-1 rounded-md border border-red-600 hover:bg-red-50"
                          title="Delete Set"
                        >
                          X
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => handleAddSet(exIdx)}
                  className="mt-3 inline-flex items-center text-sm text-red-600 hover:text-black"
                >
                  <span className="text-lg mr-1">＋</span> Add Set
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer (sticky/always visible) */}
        <div className="px-6 pt-2 pb-6 sticky bottom-0 bg-white z-10 border-t">
          <button
            className="w-full bg-[#e53935] text-white py-2 rounded-md hover:bg-black mb-3"
            onClick={() => {
              setMenuKey((k) => k + 1); // re-mount Select so menu starts at top
              setIsExerciseModalOpen(true);
            }}
          >
            Add Exercise
          </button>
          <button
            className={`w-full text-white py-2 rounded-md ${
              saving ? "opacity-70 cursor-not-allowed" : "hover:bg-black"
            }`}
            style={{ backgroundColor: "#35E539" }}
            onClick={requestFinish}
            disabled={saving} // keep enabled when invalid to show the banner
            aria-busy={saving}
          >
            {saving ? "Saving…" : "Finish"}
          </button>
        </div>

        {/* Nested Exercise Selector */}
        {isExerciseModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            {/* Responsive fix: min-width only on sm+ */}
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md sm:min-w-[420px] max-h-[75vh] overflow-y-auto p-6 relative">
              <button
                onClick={() => setIsExerciseModalOpen(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-xl font-bold"
              >
                ✕
              </button>

              <h3 className="text-lg font-bold mb-4">Select Exercise</h3>

              <CreatableSelect
                key={menuKey}
                options={exerciseOptions}
                value={selectedExercise}
                onChange={setSelectedExercise}
                onCreateOption={handleCreateExercise}
                isValidNewOption={isValidNewOption}
                formatCreateLabel={(input) => `Add "${sanitizeExerciseName(input)}"`}
                noOptionsMessage={() =>
                  "Type to search or add (letters & numbers only, max 40)"
                }
                placeholder="Search for an exercise..."
                isSearchable
                autoFocus
                menuShouldScrollIntoView={false}
                components={{ MenuList }}
                maxMenuHeight={360}
                menuPosition="fixed"
                menuPortalTarget={
                  typeof window !== "undefined" ? document.body : null
                }
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 100000 }),
                }}
              />

              <button
                className="w-full bg-[#e53935] text-white py-2 rounded-md hover:bg-black mt-4"
                onClick={handleAddExercise}
              >
                Add
              </button>
            </div>
          </div>
        )}

        {/* Close confirmation overlay */}
        {isCloseConfirmOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
              <h3 className="text-lg font-bold mb-2">Close workout?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to close this workout?
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 border border-gray-300 rounded-md py-2 hover:bg-gray-50"
                  onClick={() => setIsCloseConfirmOpen(false)}
                >
                  Keep Editing
                </button>
                <button
                  className="flex-1 bg-[#e53935] text-white rounded-md py-2 hover:bg-black"
                  onClick={confirmClose}
                >
                  Yes, Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Finish confirmation overlay */}
        {isFinishConfirmOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
              <h3 className="text-lg font-bold mb-2">Finish workout?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to save and finish this workout?
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 border border-gray-300 rounded-md py-2 hover:bg-gray-50"
                  onClick={() => setIsFinishConfirmOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 bg-[#35E539] text-white rounded-md py-2 hover:bg-black"
                  onClick={confirmFinish}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Yes, Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
